import { useState, useEffect, useCallback } from 'react';

export interface UseFileSelectionParams {
  /** All currently visible items — used to compute allSelected and auto-clear on navigation */
  items: Array<{ key: string }>;
}

export interface UseFileSelectionReturn {
  selectedFiles: Set<string>;
  selectedCount: number;
  hasSelection: boolean;
  allSelected: boolean;
  isSelected: (key: string) => boolean;
  toggleSelection: (key: string) => void;
  selectAll: () => void;
  clearSelection: () => void;
}

/**
 * Manages multi-file selection state for the file browser.
 * Automatically clears selections when the `items` array reference changes
 * (indicating folder navigation).
 */
export function useFileSelection({ items }: UseFileSelectionParams): UseFileSelectionReturn {
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());

  // Auto-clear on folder navigation (items reference changes)
  useEffect(() => {
    setSelectedFiles(new Set());
  }, [items]);

  const toggleSelection = useCallback((key: string) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedFiles(new Set(items.map(i => i.key)));
  }, [items]);

  const clearSelection = useCallback(() => {
    setSelectedFiles(new Set());
  }, []);

  const isSelected = useCallback((key: string) => selectedFiles.has(key), [selectedFiles]);

  const selectedCount = selectedFiles.size;
  const hasSelection = selectedCount > 0;
  const allSelected = items.length > 0 && selectedCount === items.length;

  return {
    selectedFiles,
    selectedCount,
    hasSelection,
    allSelected,
    isSelected,
    toggleSelection,
    selectAll,
    clearSelection,
  };
}
